/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars -- Step logic relies on evolving API/contracts; safe cleanup deferred. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'; // Components
import {
  Badge,
  Button,
  Callout,
  CardHeader,
  EmptyContent,
  Flex,
  Spinner,
  Table,
  Text,
  TextField,
} from '@/components/ui';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconRefresh,
  IconSearch,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { FormMappingCard } from '../FormMappingCard'; // API services
// Utils
import { buildFormTree } from '../utils/formTreeUtils'; // Components
import { AutoMappingToggle } from '../AutoMappingToggle'; // Mapping Engine
import { MappingEngineAdapters } from '../../mapping-engine/adapters';
import { MappingEngine } from '../../mapping-engine/MappingEngine'; // Types
import {
  ColumnMappingType,
  FileToFormMapping,
  StepComponentProps,
} from '../types';
import { ColumnMappingConfirmationModal } from '../ColumnMappingConfirmationModal';
import { loadColumnMappingPreference } from '../../utils/columnMappingPreferences';
import clsx from 'clsx'; // Local file-to-form matching function

// Helper function to get metadata label from type
const getMetadataLabel = (metadataType: string): string => {
  const metadataLabels: Record<string, string> = {
    externalSubjectId: 'External Subject ID',
    subjectNumber: 'Subject Number',
    siteNumber: 'Site Number',
    formId: 'Form ID',
    mainFormId: 'Main Form ID',
    siteName: 'Site Name',
    subjectData: 'Subject Data',
    deletedForm: 'Deleted Form',
    formIndex: 'Form Index',
    site: 'Site',
    trash: 'Trash',
  };
  return metadataLabels[metadataType] || metadataType;
};

// Local file-to-form matching function
const autoMatchFileToForm = (fileName: string, forms: any[]) => {
  // Helper function to calculate Levenshtein distance
  const levenshteinDistance = (a: string, b: string): number => {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix = [];
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1, // insertion
            matrix[i - 1][j] + 1 // deletion
          );
        }
      }
    }
    return matrix[b.length][a.length];
  };

  // Helper function to clean filename with different levels of cleaning
  const cleanFilename = (
    filename: string,
    level: 'minimal' | 'moderate' | 'aggressive' = 'moderate'
  ) => {
    let cleaned = filename.toLowerCase().replace(/\.csv$/i, '');

    switch (level) {
      case 'minimal':
        // Only remove .csv extension
        break;
      case 'moderate':
        // Remove common prefixes and suffixes
        cleaned = cleaned
          .replace(/^site_\d+_/, '') // site_1_, site_123_, etc.
          .replace(/^anumana_/, '') // anumana_ prefix
          .replace(/_v\d+$/, '') // _v1, _v2, etc.
          .replace(/_data$/, ''); // _data suffix for files
        break;
      case 'aggressive':
        // Remove all prefixes and suffixes
        cleaned = cleaned
          .replace(/^[a-z0-9]+_/, '') // any prefix ending with _
          .replace(/_\d+[a-z]+\d+$/, '') // date suffixes
          .replace(/_v\d+$/, '') // version suffixes
          .replace(/_\d+$/, ''); // numeric suffixes
        break;
    }
    return cleaned;
  };

  const originalFileName = fileName;
  const candidates: Array<{
    form: any;
    score: number;
    matchType: 'exact' | 'fuzzy';
    reason: string;
    priority: number; // Higher priority = better match
  }> = [];

  // Step 1: Try different cleaning levels and find matches
  for (const form of forms) {
    const formName = form.name.toLowerCase();
    let bestScore = 0;
    let bestMatchType: 'exact' | 'fuzzy' = 'fuzzy';
    let bestReason = '';
    let priority = 0;

    // Try minimal cleaning first (most specific)
    const fileMinimal = cleanFilename(originalFileName, 'minimal');
    if (fileMinimal === formName) {
      bestScore = 1.0;
      bestMatchType = 'exact';
      bestReason = 'Exact match after minimal cleaning';
      priority = 100; // Highest priority for exact matches
    }

    // Try moderate cleaning
    if (bestScore === 0) {
      const fileModerate = cleanFilename(originalFileName, 'moderate');
      if (fileModerate === formName) {
        bestScore = 0.95;
        bestMatchType = 'exact';
        bestReason = 'Exact match after moderate cleaning';
        priority = 95;
      } else if (fileModerate && formName.includes(fileModerate)) {
        // Check if file name is contained in form name or vice versa
        const fileWords = fileModerate.split('_');
        const formWords = formName.split('_');

        if (fileWords.every((word: string) => formWords.includes(word))) {
          bestScore = 0.9;
          bestMatchType = 'exact';
          bestReason = `File words subset of form (${fileWords.length}/${formWords.length} words)`;
          priority = 90;
        } else if (
          formWords.every((word: string) => fileWords.includes(word))
        ) {
          bestScore = 0.85;
          bestMatchType = 'exact';
          bestReason = `Form words subset of file (${formWords.length}/${fileWords.length} words)`;
          priority = 85;
        }
      }
    }

    // Try aggressive cleaning if still no match
    if (bestScore === 0) {
      const fileAggressive = cleanFilename(originalFileName, 'aggressive');
      if (fileAggressive === formName) {
        bestScore = 0.8;
        bestMatchType = 'exact';
        bestReason = 'Exact match after aggressive cleaning';
        priority = 80;
      }
    }

    // Fuzzy matching with Levenshtein distance
    if (bestScore === 0) {
      const fileModerate = cleanFilename(originalFileName, 'moderate');
      const distance = levenshteinDistance(fileModerate, formName);
      const maxLength = Math.max(fileModerate.length, formName.length);
      const similarity = 1 - distance / maxLength;

      if (similarity >= 0.8 && distance <= 2) {
        // Very similar with small distance
        bestScore = similarity * 0.7;
        bestMatchType = 'fuzzy';
        bestReason = `High similarity (${Math.round(similarity * 100)}%, distance: ${distance})`;
        priority = 70;
      } else if (similarity >= 0.6) {
        bestScore = similarity * 0.5;
        bestMatchType = 'fuzzy';
        bestReason = `Moderate similarity (${Math.round(similarity * 100)}%, distance: ${distance})`;
        priority = 50;
      }
    }

    // Word-based matching as last resort
    if (bestScore === 0) {
      const fileWords = cleanFilename(originalFileName, 'moderate').split('_');
      const formWords = formName.split('_');
      const matchingWords = formWords.filter((word: string) =>
        fileWords.some(
          (fileWord: string) =>
            word === fileWord || levenshteinDistance(word, fileWord) <= 1
        )
      );

      if (matchingWords.length > 0) {
        const matchRatio =
          matchingWords.length / Math.max(formWords.length, fileWords.length);
        if (matchRatio >= 0.5) {
          bestScore = matchRatio * 0.4;
          bestMatchType = 'fuzzy';
          bestReason = `${matchingWords.length}/${Math.max(formWords.length, fileWords.length)} words match`;
          priority = 30;
        }
      }
    }

    if (bestScore > 0) {
      candidates.push({
        form,
        score: bestScore,
        matchType: bestMatchType,
        reason: bestReason,
        priority,
      });
    }
  }

  // Step 2: Find the best candidate using priority then score
  if (candidates.length === 0) {
    return null;
  }

  // Sort by priority first, then by score
  candidates.sort((a, b) => {
    if (a.priority !== b.priority) {
      return b.priority - a.priority; // Higher priority first
    }
    return b.score - a.score; // Higher score first
  });

  const winner = candidates[0];

  // Only return if we have a good match
  if (winner.priority >= 70 || winner.score >= 0.8) {
    return {
      formId: winner.form.id.toString(),
      confidence: winner.score,
      matchType: winner.matchType,
    };
  } else {
    return null;
  }
};

type FormDetectionStepProps = StepComponentProps;

// Helper function to get fileId from SelectedFile
const getFileId = (file: any): string => {
  return file.source === 'uploaded'
    ? file.file!.name
    : `csv-${file.csvFile!.id}`;
};

export const FormDetectionStep: React.FC<FormDetectionStepProps> = ({
  wizardState,
  onUpdateState,
  onNext,
  onPrevious,
  canGoNext,
  canGoPrevious,
  currentStep,
  totalSteps,
  loadingForms = false,
}) => {
  const { selectedFiles, fileToFormMappings, availableForms, columnMappings } =
    wizardState;

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const autoMatchSuppressedRef = useRef(false);

  // Column mapping confirmation modal state
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [pendingColumnMapping, setPendingColumnMapping] = useState<{
    fileId: string;
    columnName: string;
    type: ColumnMappingType;
    metadataTypeOrTargetField?: string;
    confidence?: number;
    metadataTypeLabel?: string;
  } | null>(null);

  // Use loaded forms or empty array as fallback (stable reference for hooks deps)
  const forms = useMemo(() => availableForms || [], [availableForms]);

  // Forms error state (derived from whether forms loaded successfully)
  const formsError =
    Array.isArray(availableForms) && availableForms.length === 0
      ? 'No forms found for this study. Please contact your administrator.'
      : null;

  const handleAutoMatch = useCallback(async () => {
    autoMatchSuppressedRef.current = false;
    if (loadingForms || !forms || forms.length === 0) {
      return; // Don't run auto-match if forms are still loading or empty
    }

    // Use new MappingEngine for smart auto-mapping (forms + columns)
    const autoMappings: any[] = [];
    const newColumnMappings: any[] = [];

    // Process each file sequentially to avoid freezing the browser
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];

      // Add small delay between files to keep UI responsive
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      const mappingEngineFile =
        MappingEngineAdapters.selectedFileToMappingEngineFile(file);
      const mappingEngineForms = forms.map(f =>
        MappingEngineAdapters.studyFormToMappingEngineForm(f)
      );

      // Get the detected form using smartAutoMap which includes form detection
      const result = MappingEngine.smartAutoMap(
        mappingEngineFile,
        mappingEngineForms
      );
      const detectedForm = result.formDetected
        ? mappingEngineForms.find(f => f.id === result.fileMapping.formId)
        : null;

      if (detectedForm) {
        // Create initial mapping
        const initialMapping = MappingEngine.createMapping(
          file.file?.name || file.csvFile?.filename || 'unknown',
          detectedForm
        );

        // Auto-map columns using available columns from file metadata and sample data
        const finalMapping = MappingEngine.autoMapColumns(
          initialMapping,
          detectedForm,
          file.metadata.columns, // Pass actual columns from file
          undefined, // sampleData will be taken from file.headerData
          file // Pass file for access to headerData
        );

        // Convert to legacy format for file-to-form mappings
        const fileId =
          file.source === 'uploaded'
            ? file.file!.name
            : `csv-${file.csvFile!.id}`;

        autoMappings.push({
          fileId,
          formId: finalMapping.formId,
          confidence: result.formConfidence,
          matchType: finalMapping.matchType as 'exact' | 'fuzzy' | 'manual',
        });

        // Convert column mappings to legacy format
        Object.values(finalMapping.columnMappings).forEach(
          (columnMapping: any) => {
            const legacyMapping = {
              fileId,
              columnName: columnMapping.columnName,
              mappingType: columnMapping.mapping.type,
              confidence: columnMapping.confidence,
              targetField:
                columnMapping.mapping.type === 'formField'
                  ? columnMapping.mapping.targetField?.dataPoint
                  : undefined,
              metadataType:
                columnMapping.mapping.type === 'metadata'
                  ? {
                      type: columnMapping.mapping.metadataType,
                      label: getMetadataLabel(
                        columnMapping.mapping.metadataType
                      ),
                      description: '',
                    }
                  : undefined,
            };
            newColumnMappings.push(legacyMapping);
          }
        );
      }
    }

    onUpdateState({
      fileToFormMappings: autoMappings,
      columnMappings: newColumnMappings,
      availableForms: forms,
    });
  }, [selectedFiles, forms, loadingForms, onUpdateState]);

  // Auto-mapping is now only triggered manually via the "Auto-Match" button
  // No automatic triggering when entering the step

  // Filter files based on search query
  const filteredFiles = useMemo(() => {
    if (!searchQuery.trim()) {
      return selectedFiles;
    }

    const query = searchQuery.toLowerCase();
    return selectedFiles.filter(file => {
      const fileName =
        file.source === 'uploaded'
          ? file.file!.name.toLowerCase()
          : file.csvFile!.filename.toLowerCase();

      return fileName.includes(query);
    });
  }, [selectedFiles, searchQuery]);

  // Calculate mapping statistics
  const mappingStats = useMemo(() => {
    const totalFiles = selectedFiles.length;
    const mappedFiles = fileToFormMappings.length;
    const unmappedFiles = totalFiles - mappedFiles;

    if (!forms.length) {
      return {
        totalFiles,
        mappedFiles,
        unmappedFiles,
        rootFormMappings: 0,
        subformMappings: 0,
        autoMappings: 0,
        isComplete: false,
      };
    }

    const formTree = buildFormTree(forms);

    // Create a set of root form IDs for quick lookup
    const rootFormIds = new Set(formTree.map(root => root.id));

    const rootFormMappings = fileToFormMappings.filter(mapping => {
      return rootFormIds.has(mapping.formId);
    }).length;

    const subformMappings = fileToFormMappings.filter(mapping => {
      return !rootFormIds.has(mapping.formId);
    }).length;

    const autoMappings = fileToFormMappings.filter(
      mapping => mapping.matchType === 'exact' || mapping.matchType === 'fuzzy'
    ).length;

    return {
      totalFiles,
      mappedFiles,
      unmappedFiles,
      rootFormMappings,
      subformMappings,
      autoMappings,
      isComplete: unmappedFiles === 0 && totalFiles > 0,
    };
  }, [selectedFiles, fileToFormMappings, forms]);

  // Auto-match files on component mount (only after forms are loaded and auto-mapping is enabled)
  useEffect(() => {
    if (
      selectedFiles.length > 0 &&
      fileToFormMappings.length === 0 &&
      forms &&
      !loadingForms &&
      !autoMatchSuppressedRef.current
    ) {
      if (wizardState.autoMappingEnabled) {
        handleAutoMatch();
      }
    }
  }, [
    selectedFiles,
    fileToFormMappings.length,
    forms,
    loadingForms,
    wizardState.autoMappingEnabled,
    handleAutoMatch,
  ]);

  const handleMappingChange = useCallback(
    (fileName: string, formId: string | number | null, nestedPath?: string) => {
      const updatedMappings = [...fileToFormMappings];

      if (formId === null) {
        // Remove mapping
        const index = updatedMappings.findIndex(m => m.fileId === fileName);
        if (index >= 0) {
          updatedMappings.splice(index, 1);
        }
      } else {
        // Add or update mapping
        const existingIndex = updatedMappings.findIndex(
          m => m.fileId === fileName
        );
        const newMapping: FileToFormMapping = {
          fileId: fileName,
          formId: typeof formId === 'string' ? parseInt(formId) : formId, // Convert string to number for API compatibility
          confidence: 1.0,
          matchType: 'manual',
          // Include nested_path if provided, otherwise don't set it (will be undefined and can be deleted)
          ...(nestedPath && { nested_path: nestedPath }),
        };

        if (existingIndex >= 0) {
          updatedMappings[existingIndex] = newMapping;
        } else {
          updatedMappings.push(newMapping);
        }
      }

      onUpdateState({ fileToFormMappings: updatedMappings });
    },
    [fileToFormMappings, onUpdateState]
  );

  const handleClearAllMappings = useCallback(() => {
    autoMatchSuppressedRef.current = true;
    onUpdateState({ fileToFormMappings: [], columnMappings: [] });
  }, [onUpdateState]);

  const handlePreviewFile = useCallback((file: any) => {
    // TODO: Implement file preview
  }, []);

  const handleRemoveFile = useCallback(
    (file: any) => {
      const updatedFiles = selectedFiles.filter(f => {
        const fileId =
          f.source === 'uploaded' ? f.file!.name : `csv-${f.csvFile!.id}`;
        const currentFileId =
          file.source === 'uploaded'
            ? file.file!.name
            : `csv-${file.csvFile!.id}`;
        return fileId !== currentFileId;
      });
      const currentFileId =
        file.source === 'uploaded'
          ? file.file!.name
          : `csv-${file.csvFile!.id}`;
      const updatedMappings = fileToFormMappings.filter(
        m => m.fileId !== currentFileId
      );

      onUpdateState({
        selectedFiles: updatedFiles,
        fileToFormMappings: updatedMappings,
      });
    },
    [selectedFiles, fileToFormMappings, onUpdateState]
  );

  // Find orphaned mappings (mappings without corresponding files)
  const findOrphanedMappings = useCallback((): string[] => {
    const existingFileIds = new Set(selectedFiles.map(file => getFileId(file)));
    return fileToFormMappings
      .filter(mapping => !existingFileIds.has(mapping.fileId))
      .map(mapping => mapping.fileId);
  }, [selectedFiles, fileToFormMappings]);

  // Cleanup orphaned mappings
  const handleCleanupOrphanedMappings = useCallback(() => {
    const orphanedFileIds = findOrphanedMappings();
    if (orphanedFileIds.length === 0) return;

    const updatedFileToFormMappings = fileToFormMappings.filter(
      m => !orphanedFileIds.includes(m.fileId)
    );
    const updatedColumnMappings = (columnMappings || []).filter(
      m => !orphanedFileIds.includes(m.fileId)
    );

    onUpdateState({
      fileToFormMappings: updatedFileToFormMappings,
      columnMappings: updatedColumnMappings,
    });
  }, [findOrphanedMappings, fileToFormMappings, columnMappings, onUpdateState]);

  // Apply column classification to specific files
  const applyColumnClassification = useCallback(
    (
      fileId: string,
      columnName: string,
      type: ColumnMappingType,
      metadataTypeOrTargetField?: string,
      confidence?: number,
      applyToAll: boolean = false
    ) => {
      const updatedColumnMappings = [...(wizardState.columnMappings || [])];

      const filesToUpdate = applyToAll
        ? selectedFiles.filter(file => {
            const currentFileId =
              file.source === 'uploaded'
                ? file.file!.name
                : `csv-${file.csvFile!.id}`;
            return (
              file.metadata.columns.includes(columnName) &&
              currentFileId !== fileId
            );
          })
        : [];

      // Apply to the specified file
      const targetFile = selectedFiles.find(file => {
        const currentFileId =
          file.source === 'uploaded'
            ? file.file!.name
            : `csv-${file.csvFile!.id}`;
        return currentFileId === fileId;
      });

      if (targetFile && targetFile.metadata.columns.includes(columnName)) {
        const existingIndex = updatedColumnMappings.findIndex(
          m => m.fileId === fileId && m.columnName === columnName
        );

        const newMapping = {
          fileId,
          columnName,
          mappingType: type,
          confidence: confidence ?? 0.5,
          targetField:
            type === 'formField' ? metadataTypeOrTargetField : undefined,
          metadataType:
            type === 'metadata' && metadataTypeOrTargetField
              ? {
                  type: metadataTypeOrTargetField as any,
                  label: getMetadataLabel(metadataTypeOrTargetField),
                  description: '',
                }
              : undefined,
        };

        if (existingIndex >= 0) {
          updatedColumnMappings[existingIndex] = newMapping;
        } else {
          updatedColumnMappings.push(newMapping);
        }
      }

      // Apply to all similar files if applyToAll is true
      filesToUpdate.forEach(file => {
        const currentFileId =
          file.source === 'uploaded'
            ? file.file!.name
            : `csv-${file.csvFile!.id}`;
        const existingIndex = updatedColumnMappings.findIndex(
          m => m.fileId === currentFileId && m.columnName === columnName
        );

        const newMapping = {
          fileId: currentFileId,
          columnName,
          mappingType: type,
          confidence: confidence ?? 0.5,
          targetField:
            type === 'formField' ? metadataTypeOrTargetField : undefined,
          metadataType:
            type === 'metadata' && metadataTypeOrTargetField
              ? {
                  type: metadataTypeOrTargetField as any,
                  label: getMetadataLabel(metadataTypeOrTargetField),
                  description: '',
                }
              : undefined,
        };

        if (existingIndex >= 0) {
          updatedColumnMappings[existingIndex] = newMapping;
        } else {
          updatedColumnMappings.push(newMapping);
        }
      });

      onUpdateState({ columnMappings: updatedColumnMappings });
    },
    [selectedFiles, wizardState.columnMappings, onUpdateState]
  );

  const handleColumnClassify = useCallback(
    (
      columnName: string,
      type: ColumnMappingType,
      metadataTypeOrTargetField?: string,
      confidence?: number,
      fileId?: string, // Optional fileId to apply only to specific file
      skipBulkConfirmation?: boolean // Skip showing bulk confirmation modal
    ) => {
      // If fileId is provided, check if there are other files with this column
      if (fileId && !skipBulkConfirmation) {
        const filesWithColumn = selectedFiles.filter(file => {
          const currentFileId =
            file.source === 'uploaded'
              ? file.file!.name
              : `csv-${file.csvFile!.id}`;
          return (
            file.metadata.columns.includes(columnName) &&
            currentFileId !== fileId
          );
        });

        // If there are other files with this column, check for saved preference
        if (filesWithColumn.length > 0) {
          const savedPreference = loadColumnMappingPreference();

          // If there's a saved preference, apply it automatically
          if (savedPreference.applyToAll !== null) {
            applyColumnClassification(
              fileId,
              columnName,
              type,
              metadataTypeOrTargetField,
              confidence,
              savedPreference.applyToAll
            );
            return;
          }

          // Otherwise, show confirmation modal
          setPendingColumnMapping({
            fileId,
            columnName,
            type,
            metadataTypeOrTargetField,
            confidence,
            metadataTypeLabel:
              type === 'metadata' && metadataTypeOrTargetField
                ? getMetadataLabel(metadataTypeOrTargetField)
                : undefined,
          });
          setConfirmationModalOpen(true);
          return;
        }
      }

      // If no fileId or no other files with this column, apply directly
      // For backward compatibility, if no fileId, apply to all files (old behavior)
      if (!fileId) {
        const updatedColumnMappings = [...(wizardState.columnMappings || [])];

        selectedFiles.forEach(file => {
          if (file.metadata.columns.includes(columnName)) {
            const currentFileId =
              file.source === 'uploaded'
                ? file.file!.name
                : `csv-${file.csvFile!.id}`;
            const existingIndex = updatedColumnMappings.findIndex(
              m => m.fileId === currentFileId && m.columnName === columnName
            );

            const newMapping = {
              fileId: currentFileId,
              columnName,
              mappingType: type,
              confidence: confidence ?? 0.5,
              targetField:
                type === 'formField' ? metadataTypeOrTargetField : undefined,
              metadataType:
                type === 'metadata' && metadataTypeOrTargetField
                  ? {
                      type: metadataTypeOrTargetField as any,
                      label: getMetadataLabel(metadataTypeOrTargetField),
                      description: '',
                    }
                  : undefined,
            };

            if (existingIndex >= 0) {
              updatedColumnMappings[existingIndex] = newMapping;
            } else {
              updatedColumnMappings.push(newMapping);
            }
          }
        });

        onUpdateState({ columnMappings: updatedColumnMappings });
      } else {
        // Apply only to the specified file
        applyColumnClassification(
          fileId,
          columnName,
          type,
          metadataTypeOrTargetField,
          confidence,
          false
        );
      }
    },
    [
      selectedFiles,
      wizardState.columnMappings,
      onUpdateState,
      applyColumnClassification,
    ]
  );

  const handleConfirmationModalConfirm = useCallback(
    (applyToAll: boolean) => {
      if (!pendingColumnMapping) return;

      applyColumnClassification(
        pendingColumnMapping.fileId,
        pendingColumnMapping.columnName,
        pendingColumnMapping.type,
        pendingColumnMapping.metadataTypeOrTargetField,
        pendingColumnMapping.confidence,
        applyToAll
      );

      setPendingColumnMapping(null);
      setConfirmationModalOpen(false);
    },
    [pendingColumnMapping, applyColumnClassification]
  );

  const canProceed = mappingStats.isComplete;

  return (
    <Flex direction="column" gap="4" flexGrow="1">
      {/* Mapping Summary */}
      {/* Step Header with Summary */}
      <Flex justify="between" align="center">
        <CardHeader
          size="large"
          cardTitle="Form Detection"
          description="Map each CSV file to a specific EDC form"
        />

        {/* Forms Loading/Error State */}
        {loadingForms && (
          <Callout
            color="info"
            icon={<Spinner size="2" />}
            title="Loading Forms ..."
          />
        )}

        <AutoMappingToggle
          enabled={wizardState.autoMappingEnabled}
          onChange={enabled => onUpdateState({ autoMappingEnabled: enabled })}
        />
      </Flex>

      {formsError && (
        <Callout color="error" title="Forms Loading Issue">
          <Text size="2xsmall">{formsError}</Text>
        </Callout>
      )}
      {selectedFiles.length > 0 && (
        <>
          <Flex justify="start" align="center" gap="2">
            <Flex>
              <TextField
                placeholder="Search files by name"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                startSlot={<IconSearch />}
                size="small"
                className="data-table-toolbar__search-input"
                // Reset filter button
                endSlot={
                  searchQuery && (
                    <Button
                      variant="soft"
                      size="xsmall"
                      isIconOnly
                      color="error"
                      className="data-table-toolbar__reset"
                      title={'Reset'}
                      onClick={() => setSearchQuery('')}
                    >
                      <IconX />
                    </Button>
                  )
                }
              />
            </Flex>
            <Flex gap="2" wrap="wrap" ml="auto" mr="4">
              <Badge highContrast size="xsmall">
                Total Files : {mappingStats.totalFiles}
              </Badge>
              <Badge size="xsmall">
                Mapped Files: {mappingStats.mappedFiles}
              </Badge>
              <Badge size="xsmall">
                Root Forms: {mappingStats.rootFormMappings}
              </Badge>
              <Badge size="xsmall">
                Subforms: {mappingStats.subformMappings}
              </Badge>

              <Flex gap="1">
                <Text ml="2" size="2xsmall">
                  Status:
                </Text>
                <Badge size="xsmall" color="success" variant="surface">
                  {mappingStats.mappedFiles} Mapped
                </Badge>
                {mappingStats.unmappedFiles > 0 && (
                  <Badge size="xsmall" color="warning" variant="surface">
                    {mappingStats.unmappedFiles} Unmapped
                  </Badge>
                )}
                {mappingStats.autoMappings > 0 && (
                  <Badge size="xsmall" color="info" variant="surface">
                    {mappingStats.autoMappings} Auto-matched
                  </Badge>
                )}
              </Flex>
            </Flex>

            {/* Bulk Actions */}
            <Flex gap="2">
              <Button
                variant="surface"
                size="small"
                color="error"
                onClick={handleClearAllMappings}
                startIcon={<IconRefresh />}
                disabled={fileToFormMappings.length === 0}
              >
                Clear All
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={handleAutoMatch}
                startIcon={<IconWand />}
                disabled={loadingForms}
              >
                {loadingForms ? 'Loading Forms...' : 'Auto-Match All'}
              </Button>
            </Flex>
          </Flex>
        </>
      )}

      {/* File to Form Mapping Cards */}
      <Table.Root
        columns="auto 1fr auto 1fr auto"
        variant="surface"
        className={clsx(
          ' form-detection-table table-striped',
          selectedFiles.length > 0 &&
            filteredFiles.length === 0 &&
            'data-table-empty'
        )}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>#</Table.HeaderCell>
            <Table.HeaderCell>File</Table.HeaderCell>
            <Table.HeaderCell> </Table.HeaderCell>
            <Table.HeaderCell>Form</Table.HeaderCell>
            <Table.HeaderCell> </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {selectedFiles.length > 0 ? (
            filteredFiles.length > 0 ? (
              <>
                {filteredFiles.map((file, index) => {
                  const fileId =
                    file.source === 'uploaded'
                      ? file.file!.name
                      : `csv-${file.csvFile!.id}`;
                  const mapping = fileToFormMappings.find(
                    m => m.fileId === fileId
                  );

                  // Wrapper function to pass fileId to handleColumnClassify
                  const handleColumnClassifyWithFileId = (
                    columnName: string,
                    type: ColumnMappingType,
                    metadataTypeOrTargetField?: string,
                    skipBulkConfirmation?: boolean
                  ) => {
                    handleColumnClassify(
                      columnName,
                      type,
                      metadataTypeOrTargetField,
                      undefined, // confidence
                      fileId,
                      skipBulkConfirmation
                    );
                  };

                  return (
                    <FormMappingCard
                      index={String(index + 1)}
                      key={fileId}
                      file={file}
                      availableForms={forms}
                      mapping={mapping}
                      columnMappings={wizardState.columnMappings}
                      onMappingChange={handleMappingChange}
                      onColumnClassify={handleColumnClassifyWithFileId}
                      onUpdateState={onUpdateState}
                      onPreviewFile={handlePreviewFile}
                      onRemoveFile={handleRemoveFile}
                      autoMappingEnabled={wizardState.autoMappingEnabled}
                    />
                  );
                })}
              </>
            ) : (
              <Table.Row className="data-table-empty-row">
                <Table.Cell className="data-table-empty-cell">
                  <EmptyContent
                    state="info"
                    size="medium"
                    icon={<IconSearch />}
                    withIconBox
                    title="No files match your search"
                    description={`No files found matching "${searchQuery}". Try adjusting your search terms.`}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => setSearchQuery('')}
                    >
                      Clear Search
                    </Button>
                  </EmptyContent>
                </Table.Cell>
              </Table.Row>
            )
          ) : (
            <Table.Row className="data-table-empty-row">
              <Table.Cell className="data-table-empty-cell">
                <EmptyContent
                  state="error"
                  size="medium"
                  icon={<IconAlertTriangle />}
                  withIconBox
                  title="No files selected"
                  description="Please go back to Step 1 and select CSV files to continue with form mapping."
                >
                  <Button
                    startIcon={<IconArrowLeft />}
                    variant="outlined"
                    onClick={onPrevious}
                  >
                    Back to File Upload
                  </Button>
                </EmptyContent>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>

      {/* Validation Messages */}
      {selectedFiles.length > 0 &&
        (!mappingStats.isComplete || mappingStats.unmappedFiles < 0) && (
          <Callout
            icon={<IconAlertTriangle />}
            size="small"
            color="warning"
            actions={
              mappingStats.unmappedFiles < 0 && (
                <Button
                  variant="outlined"
                  size="xsmall"
                  color="error"
                  onClick={handleCleanupOrphanedMappings}
                  startIcon={<IconX />}
                >
                  Remove orphaned mappings
                </Button>
              )
            }
            title={
              mappingStats.unmappedFiles < 0
                ? 'Orphaned mappings detected'
                : 'Incomplete mappings'
            }
          >
            {mappingStats.unmappedFiles < 0
              ? `${Math.abs(mappingStats.unmappedFiles)} mapping(s) exist for deleted files.`
              : `${mappingStats.unmappedFiles} file(s) are not mapped to any form.`}
            {mappingStats.unmappedFiles >= 0 &&
              ' Please map all files to continue to the next step.'}
          </Callout>
        )}

      {selectedFiles.length > 0 && mappingStats.isComplete && (
        <Callout
          variant="surface"
          size="small"
          color="success"
          icon={<IconCheck />}
          title="All files mapped"
        >
          <Text size="2xsmall">
            All files have been successfully mapped to forms. You can proceed to
            the next step.
          </Text>
        </Callout>
      )}

      {/* Column Mapping Confirmation Modal */}
      {pendingColumnMapping && (
        <ColumnMappingConfirmationModal
          open={confirmationModalOpen}
          onOpenChange={setConfirmationModalOpen}
          columnName={pendingColumnMapping.columnName}
          mappingType={pendingColumnMapping.type}
          targetField={
            pendingColumnMapping.type === 'formField'
              ? pendingColumnMapping.metadataTypeOrTargetField
              : undefined
          }
          metadataTypeLabel={pendingColumnMapping.metadataTypeLabel}
          similarFilesCount={
            selectedFiles.filter(file => {
              const currentFileId =
                file.source === 'uploaded'
                  ? file.file!.name
                  : `csv-${file.csvFile!.id}`;
              return (
                file.metadata.columns.includes(
                  pendingColumnMapping.columnName
                ) && currentFileId !== pendingColumnMapping.fileId
              );
            }).length
          }
          onConfirm={handleConfirmationModalConfirm}
        />
      )}
    </Flex>
  );
};

export default FormDetectionStep;
